import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import BulkImportDocumentMetadataWizard from '~/components/Admin/MoreSettingsPage/BulkDocumentMetadataImportWizard/BulkImportDocumentMetadataWizard';
import { PageLayout } from '~/eds/src/modules/PageLayout/PageLayout';
import { FeatureFlagType } from '~/enums/FeatureFlagType';
import { ClauseTaggingAccessSetting } from '~/features/more-settings/ClauseTaggingAccessSetting';
import { DocumentsMetadataBulkUpload } from '~/features/more-settings/DocumentsMetadataBulkUpload';
import { getInitialConfigs } from '~/features/more-settings/hooks';
import { withPermissions } from '~/hocs/withPermissions';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { testHasFlag } from '~/permissions/utils/testHasFlag';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { RoutePathType } from '~/routing/enums';
import { generateNav } from '~/routing/nav';
import { useRouting } from '~/routing/useRouting';
import { testIsSuperAdmin } from '~/utils/user';

import SessionTimeout from './session-timeout';

const PERMISSIONS = [
  {
    resourceId: 'documents_metadata_bulk_upload',
    resourceType: 'view',
  },
  {
    resourceId: 'clause_quick_ai',
    resourceType: 'view',
  },
];

interface Props {
  checkPermission: (resourceId: string, resourceType: string) => boolean;
  isPermissionsLoading: boolean;
}

const Page = ({ isPermissionsLoading, checkPermission }: Props) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const currentUser = useCurrentUser();
  const { params } = useRouting();
  const { clientId } = params;

  const isSuperAdmin = testIsSuperAdmin(currentUser);
  const hasBulkDocumentMetadataFlagEnabled = testHasFlag(
    FeatureFlagType.BulkDocumentMetadata,
  )(currentUser);

  const configs = useSelector(
    slices.moreSettings.selectors.selectConfiguration,
  );

  const { data: isWorkdayClient = false, isLoading: isLoadingIsWorkdayClient } =
    api.endpoints.getIsWorkdayClient.useQuery(
      { clientId: Number(clientId) },
      { skip: !isSuperAdmin },
    );

  const { data: isWorkdayUser = false, isLoading: isLoadingIsWorkdayUser } =
    api.endpoints.getIsWorkdayUser.useQuery({ skip: isSuperAdmin });

  const isLoading = isLoadingIsWorkdayUser || isLoadingIsWorkdayClient;
  const workdayClient = isWorkdayUser || isWorkdayClient;

  useEffect(() => {
    dispatch(
      slices.moreSettings.actions.setConfigs({
        ...configs,
        ...getInitialConfigs({ currentUser }),
      }),
    );
  }, [currentUser]);

  const nav = generateNav({
    current: {
      text: intl.formatMessage({
        id: 'AdminConsoleMoreSettings.navigationText',
        defaultMessage: 'More Settings',
        description: 'Navigation breadcrumb text for more settings page',
      }),
      pathname: RoutePathType.AdminConsoleClientMoreSettings,
    },
    from: RoutePathType.AdminConsoleClient,
    params: {
      clientId: currentUser.client,
    },
    intl,
  });

  const bullkImportProps = {
    onClose: () => {
      dispatch(
        slices.moreSettings.actions.setConfigs({
          ...configs,
          showImportWizard: false,
        }),
      );
    },
    clientId: clientId,
    handleImportedJob: (importedJobId: any) =>
      dispatch(
        slices.moreSettings.actions.setConfigs({
          ...configs,
          importedJobId,
          showImportWizard: false,
        }),
      ),
  };

  return (
    <PageLayout
      nav={nav}
      title={intl.formatMessage({
        id: 'AdminConsoleMoreSettings.pageTitle',
        defaultMessage: 'More Settings',
        description: 'Page title for more settings page',
      })}
      loadingContent={{
        message: intl.formatMessage({
          id: 'AdminConsoleMoreSettings.loadingMessage',
          defaultMessage: 'Loading more settings...',
          description: 'Loading message while more settings are being loaded',
        }),
        isLoading: isPermissionsLoading || isLoading,
      }}
    >
      {hasBulkDocumentMetadataFlagEnabled &&
        checkPermission('documents_metadata_bulk_upload', 'view') && (
          <DocumentsMetadataBulkUpload />
        )}

      {checkPermission('clause_quick_ai', 'view') && (
        <ClauseTaggingAccessSetting />
      )}

      {!workdayClient && <SessionTimeout />}

      {configs.showImportWizard && (
        <BulkImportDocumentMetadataWizard {...bullkImportProps} />
      )}
    </PageLayout>
  );
};

export default withPermissions(Page, PERMISSIONS);
